import React from 'react';
import { useParams } from 'react-router-dom';
import { Wallet, ExternalLink, Server } from 'lucide-react';
import { Line, Pie } from 'react-chartjs-2';
import { useWalletData } from '../hooks/useWalletData';
import { MarketStats } from '../components/MarketStats';
import { pieChartOptions, lineChartOptions, formatBalance, formatUsdValue, formatDate, truncateAddress } from '../utils/chartHelpers';

export function WalletPage() {
  const { address } = useParams<{ address: string }>();
  const {
    accountData,
    balanceHistory,
    stakeBalanceData,
    price,
    loading,
    error,
    lastUpdated
  } = useWalletData(address);

  const getChartData = () => {
    if (!accountData) return null;

    const freeTao = Number(BigInt(accountData.balance_free)) / 1e9;
    const stakedRootTao = Number(BigInt(accountData.balance_staked_root)) / 1e9;
    const stakedAlphaTao = Number(BigInt(accountData.balance_staked_alpha_as_tao)) / 1e9;

    return {
      labels: ['Free Balance', 'Staked (Root)', 'Staked (Alpha)'],
      datasets: [
        {
          data: [freeTao, stakedRootTao, stakedAlphaTao],
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(99, 102, 241, 0.8)',
            'rgba(219, 39, 119, 0.8)',
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(99, 102, 241, 1)',
            'rgba(219, 39, 119, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const getNetworkDistributionData = () => {
    if (!stakeBalanceData.length) return null;

    const data = stakeBalanceData
      .filter(item => item.netuid !== 0)
      .map(item => ({
        netuid: item.netuid,
        balance: Number(BigInt(item.balance_as_tao)) / 1e9,
        rawBalance: Number(BigInt(item.balance)) / 1e9
      }));

    return {
      labels: data.map(item => `SN ${item.netuid}`),
      datasets: [
        {
          data: data.map(item => item.balance),
          rawBalances: data.map(item => item.rawBalance),
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(219, 39, 119, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(16, 185, 129, 0.8)',
          ],
          borderColor: [
            'rgba(99, 102, 241, 1)',
            'rgba(219, 39, 119, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(16, 185, 129, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const getBalanceHistoryData = () => {
    if (!balanceHistory.length) return null;

    return {
      labels: balanceHistory.map(item => new Date(item.timestamp)),
      datasets: [
        {
          label: 'Total Balance',
          data: balanceHistory.map(item => Number(BigInt(item.balance_total)) / 1e9),
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        }
      ],
    };
  };

  if (error) {
    return (
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-black/20 rounded-lg"></div>
          <div className="h-12 bg-black/20 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!accountData) {
    return (
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="text-center text-gray-400">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 space-y-4 md:space-y-0">
        <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-3">
          <div className="flex items-center space-x-3">
            <Wallet className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
            <h1 className="text-xl md:text-2xl font-bold">TAO Wallet Dashboard</h1>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <span className="hidden md:inline text-gray-400">|</span>
            <p className="font-mono text-gray-400">{truncateAddress(accountData.address.ss58)}</p>
            <a
              href={`https://bittensor.com/scan/address/${accountData.address.ss58}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-600 hover:text-red-700"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="group relative">
          <button
            className="flex items-center space-x-2 px-3 py-2 bg-red-600/50 cursor-not-allowed rounded-lg text-sm font-medium transition-colors opacity-75"
            disabled
          >
            <Server className="w-4 h-4" />
            <span>View Subnets</span>
          </button>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Coming Soon
          </div>
        </div>
      </div>

      <div className="space-y-6 md:space-y-8">
        <div className="mb-6 md:mb-8">
          <p className="text-gray-400 text-base md:text-lg">Total Balance</p>
          <p className="text-3xl md:text-5xl font-bold text-green-400 mt-2 mb-2">
            {formatBalance(accountData.balance_total)} τ
          </p>
          <p className="text-xl md:text-2xl text-gray-300">
            {formatUsdValue(accountData.balance_total, price)}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <p className="text-gray-400 text-sm">Free Balance</p>
            <p className="text-xl md:text-2xl font-bold mt-1 text-blue-400">
              {formatBalance(accountData.balance_free)} τ
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {formatUsdValue(accountData.balance_free, price)}
            </p>
          </div>

          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <p className="text-gray-400 text-sm">Total Staked</p>
            <p className="text-xl md:text-2xl font-bold mt-1 text-purple-400">
              {formatBalance(accountData.balance_staked)} τ
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {formatUsdValue(accountData.balance_staked, price)}
            </p>
          </div>

          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <p className="text-gray-400 text-sm">Staked (Root)</p>
            <p className="text-xl md:text-2xl font-bold mt-1 text-indigo-400">
              {formatBalance(accountData.balance_staked_root)} τ
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {formatUsdValue(accountData.balance_staked_root, price)}
            </p>
          </div>

          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <p className="text-gray-400 text-sm">Staked (Alpha)</p>
            <p className="text-xl md:text-2xl font-bold mt-1 text-pink-400">
              {formatBalance(accountData.balance_staked_alpha_as_tao)} τ
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {formatUsdValue(accountData.balance_staked_alpha_as_tao, price)}
            </p>
          </div>
        </div>

        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
          <h2 className="text-lg md:text-xl font-semibold mb-4">Balance History</h2>
          <div className="h-[300px] md:h-[400px] bg-black/40 rounded-xl p-4">
            {getBalanceHistoryData() && <Line data={getBalanceHistoryData()!} options={lineChartOptions} />}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <h2 className="text-lg md:text-xl font-semibold mb-4">Balance Distribution</h2>
            <div className="h-[250px] md:h-[300px]">
              {getChartData() && <Pie data={getChartData()!} options={pieChartOptions} />}
            </div>
          </div>

          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
            <h2 className="text-lg md:text-xl font-semibold mb-4">Subnet Distribution</h2>
            <div className="h-[250px] md:h-[300px]">
              {getNetworkDistributionData() && <Pie data={getNetworkDistributionData()!} options={pieChartOptions} />}
            </div>
          </div>
        </div>

        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
          <p className="text-gray-400 text-sm">Last Updated</p>
          <p className="text-base md:text-lg mt-1">{formatDate(lastUpdated.toISOString())}</p>
        </div>
      </div>
    </div>
  );
}